import React, { useEffect, useState } from 'react';
import {
  Typography,
  Button,
  Box,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
  Alert,
  Snackbar,
  Divider,
  Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { addNode, deleteNode, getNodes, NodeType } from '../api/services/computeService/NodeService';
import { useKeycloak } from '@react-keycloak/web';
import worldCountries from './world-countries.json';

const DiscoverInstances = () => {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [sortBy, setSortBy] = useState('hostname');
  const [openDialog, setOpenDialog] = useState(false);
  const [newServer, setNewServer] = useState({
    hostname: '',
    ipAddress: '',
    port: "22",
    location: '',
    singleNode: true,
    slurmHead: false,
    slurmOwner: '',
    status: 0,
    numOfCpus: 0,
    cpu: '',
    numOfGpus: 0,
    gpu: '',
    gpuType: '',
    ram: 0,
    mem: 0,
    type: 0,
    os: '',
    osVersion: '',
    kernelVersion: '',
    suser: '',
    spass: '',
    spriv: ''
  });
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();

  const gpuVendors = ['NVIDIA', 'AMD'];
  const gpuModels = {
    NVIDIA: [
      'RTX 4090',
      'RTX 4080',
      'RTX 3090 Ti',
      'RTX 3090',
      'RTX 3080 Ti',
      'RTX 3080',
      'A100',
      'A6000',
      'L40',
      'H100',
      'Tesla V100',
      'Tesla P100',
      'Tesla P40', 
      'Tesla P4',
      'Quadro RTX 8000',
      'Quadro RTX 6000', 
      'Quadro RTX 4000',
      'Quadro P6000',
      'Quadro P4000',
      'A10',
      'A30',
      'A40',
      'A100 PCIe',
      'A100 SXM',
      'A100 HGX',
      'V100S',
      'V100 PCIe',
      'V100 SXM2',
      'P40',
      'P4',
      'M10',
      'M60',
      'M6',
      'K80',
      'K520',
      'GRID K2',
      'GRID K1',
      'GH200',
      'GH100',
      'GH50',
      'GH40',
      'GH30',
      'GH20',
      'H100'
    ],
    AMD: [
      'RX 7900 XTX',
      'RX 7900 XT',
      'RX 6950 XT',
      'RX 6900 XT',
      'RX 6800 XT',
      'MI300',
      'MI250',
      'MI210',
      'MI100',
      'Radeon Pro VII',
      'Radeon Pro WX 8200',
      'Radeon Pro WX 9100',
      'Radeon Instinct MI8',
      'Radeon Instinct MI6',
      'FirePro S7150',
      'FirePro S7100X',
      'AMD Radeon Pro W6800',
      'AMD Radeon Pro W6600',
      'AMD Radeon Pro W6400',
      'AMD Radeon Pro WX 8200',
      'AMD Radeon Pro WX 9100',
      'AMD Radeon Instinct MI60',
      'AMD Radeon Instinct MI50',
      'AMD Radeon Instinct MI25',
      'AMD Radeon Instinct MI8',
      'AMD Radeon Instinct MI6',
      'AMD FirePro S9170',
      'AMD FirePro S9150',
      'AMD FirePro S9050',
      'AMD FirePro S8150',
      'AMD FirePro S7150',
      'AMD FirePro S7100X',
      'AMD MI300X',
      'AMD MI200X',
      'AMD MI100X',
      'AMD MI50X',
      'AMD MI25X',
      'AMD MI8X'
    ]
  };

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serverToDelete, setServerToDelete] = useState(null);

  const countries = worldCountries.features.map(feature => ({
    id: feature.id,
    name: feature.properties.NAME
  }));
  
  useEffect(() => {
    if (keycloak.authenticated) {
      fetchServers();
    }
  }, [keycloak.authenticated]);

  const fetchServers = async () => {
    try {
      setLoading(true);
      
      if (!keycloak.token) {
        setSnackbar({
          open: true,
          message: 'Authentication required',
          severity: 'error'
        });
        return;
      }

      await keycloak.updateToken(30);
      
      const response = await getNodes({
        paginationParams: { page: 0, page_size: 100 },
        token: keycloak.token
      });
      
      if (response.success && response.data.nodes) {
        const formattedServers = response.data.nodes.map(node => ({
          id: node.id,
          hostname: node.nodeMetadata?.system_info?.hostname || `node-${node.id?.substring(0, 8)}`,
          ipAddress: node.address_details?.ip_address || 'Unknown',
          port: node.address_details?.port || '22',
          location: node.address_details?.region || 'Unknown',
          status: node.status === 1 ? 'active' : 'inactive',
          numOfCpus: node.num_of_cpus || 0,
          cpu: node.nodeMetadata?.cpu_info?.model || 'Unknown',
          numOfGpus: node.num_of_gpus || 0,
          gpu: node.gpus && node.gpus.length > 0 ? node.gpus[0].model : 'None',
          gpuType: node.gpus && node.gpus.length > 0 ? (node.gpus[0].vendor === 1 ? 'NVIDIA' : 'AMD') : 'None',
          ram: node.nodeMetadata?.memory_info?.total ? 
               Math.round(parseFloat(node.nodeMetadata.memory_info.total.replace(/[^\d.]/g, ''))) : 0,
          mem: node.nodeMetadata?.memory_info?.total ? 
               Math.round(parseFloat(node.nodeMetadata.memory_info.total.replace(/[^\d.]/g, ''))) : 0,
          type: node.type || 2,
          os: node.nodeMetadata?.os_info?.name || 'Unknown',
          osVersion: node.nodeMetadata?.os_info?.version || '',
          kernelVersion: node.nodeMetadata?.os_info?.kernel || '',
          singleNode: node.single_node || true,
          slurmHead: node.slurm_head || false,
          slurmOwner: node.slurm_owner || ''
        }));
        
        setServers(formattedServers);
      } else {
        setServers([]);
        setSnackbar({
          open: true,
          message: 'No servers found or failed to fetch servers',
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error('Error fetching servers:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch servers',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddServer = (server) => {
    console.log('Adding server:', server);
    navigate('/cloud-management');
  };

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const filteredServers = servers.filter(server =>
    server.hostname.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedServers = filteredServers.sort((a, b) => {
    if (typeof a[sortBy] === 'string') {
      return a[sortBy].localeCompare(b[sortBy]) * (sortDirection === 'asc' ? 1 : -1);
    } else {
      return (a[sortBy] - b[sortBy]) * (sortDirection === 'asc' ? 1 : -1);
    }
  });

  const validateForm = () => {
    if (!newServer.hostname || !newServer.ipAddress || !newServer.suser) {
      setSnackbar({
        open: true,
        message: 'Hostname, IP Address, and Username are required',
        severity: 'error'
      });
      return false;
    }

    if (!newServer.spass && !newServer.spriv) {
      setSnackbar({
        open: true,
        message: 'Please provide either a password or an SSH private key for authentication',
        severity: 'error'
      });
      return false;
    }

    return true;
  };

  const handleAddNewServer = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await keycloak.updateToken(30);

      const nodeData = {
        addressDetails: {
          ipAddress: newServer.ipAddress,
          port: newServer.port,
          region: newServer.location || 'us-east-1'
        },
        nodeType: newServer.type === 0 ? NodeType.NODE_TYPE_STORAGE : NodeType.NODE_TYPE_COMPUTE,
        sudoUserCreds: {
          username: newServer.suser || 'piomonitor',
          password: newServer.spass || '',
          privateKey: newServer.spriv || ''
        },
        token: keycloak.token
      };

      console.log('Adding node with data:', nodeData);

      const response = await addNode(nodeData);

      if (response.success && response.data) {
        const newServerData = {
          id: response.data.id,
          hostname: newServer.hostname,
          ipAddress: newServer.ipAddress,
          port: newServer.port,
          gpuType: newServer.gpuType,
          gpu: newServer.gpu,
          location: newServer.location,
          status: 'active',
          numCpu: newServer.numOfCpus,
          cpu: newServer.cpu,
          numGpus: newServer.numOfGpus,
          ram: newServer.ram,
          mem: newServer.mem,
          type: newServer.type,
          os: newServer.os,
          osVersion: newServer.osVersion,
          kernelVersion: newServer.kernelVersion,
          singleNode: newServer.singleNode,
          slurmHead: newServer.slurmHead,
          slurmOwner: newServer.slurmOwner
        };
        
        setServers(prevServers => [...prevServers, newServerData]);
        setOpenDialog(false);
        setNewServer({
          hostname: '',
          ipAddress: '',
          port: '22',
          location: '',
          singleNode: true,
          slurmHead: false,
          slurmOwner: '',
          status: 0,
          numOfCpus: 0,
          cpu: '',
          numOfGpus: 0,
          gpu: '',
          gpuType: '',
          ram: 0,
          mem: 0,
          type: 0,
          os: '',
          osVersion: '',
          kernelVersion: '',
          suser: '',
          spass: '',
          spriv: ''
        });
        
        setSnackbar({
          open: true,
          message: 'Server added successfully!',
          severity: 'success'
        });
      } else {
        const errorMessage = response.error || 'Failed to add server. Please try again.';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error adding server:', error);
      let errorMessage = 'An unexpected error occurred while adding the server.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    console.log('Input changed:', name, value);
    
    if (name === 'gpuType') {
      setNewServer(prev => ({
        ...prev,
        gpuType: value,
        gpu: ''
      }));
    } else {
      setNewServer(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAssignServer = async (server) => {
    try {
      console.log('Assigning server:', server.hostname);
      setSnackbar({
        open: true,
        message: `Server ${server.hostname} assigned successfully!`,
        severity: 'success'
      });
      navigate('/cloud-management');
    } catch (error) {
      console.error('Error assigning server:', error);
      setSnackbar({
        open: true,
        message: 'Failed to assign server',
        severity: 'error'
      });
    }
  };

  const handleDeleteServer = (server) => {
    setServerToDelete(server);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await keycloak.updateToken(30);
      
      const response = await deleteNode(serverToDelete.id, keycloak.token);
      
      if (response.success) {
        const updatedServers = servers.filter(s => s.id !== serverToDelete.id);
        setServers(updatedServers);
        setSnackbar({
          open: true,
          message: 'Server deleted successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: `Failed to delete server: ${response.error}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting server:', error);
      setSnackbar({
        open: true,
        message: `Error deleting server: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setServerToDelete(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" gutterBottom>
            Available Servers
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenDialog(true)}
          >
            Add Server
          </Button>
        </Box>
        <TextField
          variant="outlined"
          placeholder="Search by server name..."
          fullWidth
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'hostname'}
                    direction={sortBy === 'hostname' ? sortDirection : 'asc'}
                    onClick={() => handleSort('hostname')}
                  >
                    Hostname
                  </TableSortLabel>
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'gpuType'}
                    direction={sortBy === 'gpuType' ? sortDirection : 'asc'}
                    onClick={() => handleSort('gpuType')}
                  >
                    GPU Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'gpu'}
                    direction={sortBy === 'gpu' ? sortDirection : 'asc'}
                    onClick={() => handleSort('gpu')}
                  >
                    GPU Model
                  </TableSortLabel>
                </TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedServers.map((server) => (
                <TableRow key={server.id}>
                  <TableCell>{server.hostname}</TableCell>
                  <TableCell>
                    <Chip label={server.status === 'active' ? "Online" : "Offline"} 
                          color={server.status === 'active' ? "success" : "error"} />
                  </TableCell>
                  <TableCell>{server.ipAddress}</TableCell>
                  <TableCell>
                    {server.gpuType}
                  </TableCell>
                  <TableCell>{server.gpu}</TableCell>
                  <TableCell>
                    {countries.find(country => country.id === server.location)?.name || server.location}
                  </TableCell>
                  <TableCell>{server.type === 0 ? 'Storage' : 'Compute'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => handleAssignServer(server)}
                      >
                        Assign
                      </Button>
                      <Button 
                        disabled={false}
                        variant="contained" 
                        color="error" 
                        onClick={() => handleDeleteServer(server)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Add New Server</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Server Details
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  name="hostname"
                  label="Hostname"
                  fullWidth
                  required
                  value={newServer.hostname}
                  onChange={handleInputChange}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  name="ipAddress"
                  label="IP Address"
                  fullWidth
                  required
                  value={newServer.ipAddress}
                  onChange={handleInputChange}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  name="port"
                  label="Port"
                  type="number"
                  fullWidth
                  value={newServer.port}
                  onChange={handleInputChange}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  options={countries}
                  getOptionLabel={(option) => `${option.name} (${option.id})`}
                  value={countries.find(country => country.id === newServer.location) || null}
                  onChange={(event, newValue) => {
                    setNewServer(prev => ({
                      ...prev,
                      location: newValue ? newValue.id : ''
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="location"
                      label="Location"
                      fullWidth
                      required
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>GPU Type</InputLabel>
                  <Select
                    name="gpuType"
                    value={newServer.gpuType}
                    label="GPU Type"
                    onChange={handleInputChange}
                  >
                    <MenuItem value={0}>AMD</MenuItem>
                    <MenuItem value={1}>NVIDIA</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>GPU Model</InputLabel>
                  <Select
                    name="gpu"
                    value={newServer.gpu}
                    label="GPU Model"
                    onChange={handleInputChange}
                  >
                    {newServer.gpuType === 0 && gpuModels.AMD.map((model) => (
                      <MenuItem key={model} value={model}>{model}</MenuItem>
                    ))}
                    {newServer.gpuType === 1 && gpuModels.NVIDIA.map((model) => (
                      <MenuItem key={model} value={model}>{model}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  SSH Authentication
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Provide at least one authentication method (password or SSH key). You can provide both for flexibility.
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  name="suser"
                  label="Username"
                  fullWidth
                  required
                  value={newServer.suser}
                  onChange={handleInputChange}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  name="spass"
                  label="Password"
                  type="password"
                  fullWidth
                  value={newServer.spass}
                  onChange={handleInputChange}
                  helperText="SSH password (optional if SSH key is provided)"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="spriv"
                  label="SSH Private Key (Optional)"
                  fullWidth
                  multiline
                  rows={6}
                  value={newServer.spriv}
                  onChange={handleInputChange}
                  placeholder="-----BEGIN OPENSSH PRIVATE KEY-----
MIIEowIBAAKCAQEA...
-----END OPENSSH PRIVATE KEY-----"
                  helperText="SSH private key (optional if password is provided) - supports RSA, ECDSA, and Ed25519 keys"
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem'
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Authentication Options:</strong>
                    <br />
                    • You can provide either a password or SSH key (or both)
                    <br />
                    • If using SSH key, ensure the corresponding public key is in the server's authorized_keys
                    <br />
                    • SSH key authentication is generally more secure than password authentication
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={handleAddNewServer} variant="contained" color="primary">
              Add Server
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete server "{serverToDelete?.hostname}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default DiscoverInstances;
